import { useContext, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { FormEvent, MouseEvent } from 'react';
import { courses } from '../../shared/courses';
import { offerCategories, offerCategory } from '../../shared/offers';
import type { OfferCategory } from '../../shared/offers';
import { isCourse, offerLabels, publicLeadSchema } from '../../shared/schemas';
import type { ApiError, BookingFormat, Offer } from '../../shared/schemas';
import { firstTouchRef } from '../analytics/attribution';
import { trackGoal } from '../analytics/tracker';
import { isExcursionHidden, orderByExcursion } from '../../shared/excursions';
import { InitialDataContext } from '../initialData';

type LeadFormProps = { initialOffer: Offer | null; onLegalLink: (event: MouseEvent<HTMLAnchorElement>) => void };
type FieldErrors = Record<string, string>;

/** The switch shows only categories with something to book: master classes appear once they have offers. */
function bookableCategories(facts: Parameters<typeof isExcursionHidden>[0]) {
  // Excursions the admin hid (out of season) are not offered, the rest follow the admin's order; staff forms keep the full `offerCategories`.
  return offerCategories
    .map((category) => ({ ...category, groups: category.groups.map((group) => ({ ...group, offers: orderByExcursion(group.offers, facts, (offer) => offer).filter((offer) => !isExcursionHidden(facts, offer)) })).filter((group) => group.offers.length > 0) }))
    .filter((category) => category.groups.length > 0);
}
const selectPrompts: Record<OfferCategory, string> = { excursion: 'Выберите экскурсию', masterclass: 'Выберите мастер-класс', course: 'Выберите курс или лекцию' };
const successTexts: Record<OfferCategory, string> = {
  excursion: 'Свяжемся с вами и согласуем экскурсию для вашей группы или личного круга. Дату, стоимость и условия подтвердит организатор.',
  masterclass: 'Свяжемся с вами и обсудим участие в мастер-классе. Дату, стоимость и условия подтвердит организатор.',
  course: 'Свяжемся с вами и обсудим запись на курс. Расписание, стоимость и условия обучения подтвердит организатор.',
};

/** Option and summary text: the optgroup or the kicker already says it is a partner course. */
function offerTitle(offer: Offer): string {
  return isCourse(offer) ? courses[offer].title : offerLabels[offer];
}

export function LeadForm({ initialOffer: requestedOffer, onLegalLink }: LeadFormProps) {
  const facts = useContext(InitialDataContext).excursions;
  const categories = bookableCategories(facts);
  // An old link or button may still ask for a hidden excursion: open the picker instead.
  const initialOffer = requestedOffer && !isExcursionHidden(facts, requestedOffer) ? requestedOffer : null;
  const initialCategory = initialOffer ? offerCategory(initialOffer) : categories[0]?.id ?? 'excursion';
  const [category, setCategory] = useState<OfferCategory>(initialCategory);
  const [offer, setOffer] = useState<Offer | ''>(initialOffer ?? '');
  // A preselected offer is shown as one line with «Изменить»; the picker opens on demand.
  const [isPicking, setIsPicking] = useState(initialOffer === null);
  const [bookingFormat, setBookingFormat] = useState<BookingFormat | ''>('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [state, setState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const excursionFields = category === 'excursion';
  const groups = categories.find((item) => item.id === category)?.groups ?? [];
  useEffect(() => { formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(); }, [errors]);
  useEffect(() => { if (state === 'success') successRef.current?.focus(); }, [state]);

  function pickCategory(next: OfferCategory) {
    setCategory(next);
    if (offer !== '' && offerCategory(offer) !== next) setOffer('');
  }

  function startPicking() {
    // The summary button disappears; keep the keyboard on the picker that replaces it.
    flushSync(() => setIsPicking(true));
    formRef.current?.querySelector<HTMLElement>('.booking-segments--category input:checked, select[name="excursion"]')?.focus();
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (state === 'sending') return;
    const data = new FormData(event.currentTarget);
    // Without a chosen offer the rest is still checked against the category's first offer, so every field error shows at once.
    const excursion = offer || groups[0]!.offers[0]!;
    const common = {
      name: String(data.get('name') ?? ''), contact: String(data.get('contact') ?? ''), comment: String(data.get('comment') ?? ''),
      website: String(data.get('website') ?? ''), privacyAccepted: data.get('privacyAccepted') === 'on', ref: firstTouchRef(),
    };
    const parsed = publicLeadSchema.safeParse(excursionFields
      ? { ...common, excursion, bookingFormat, preferredPark: excursion === 'botanical_walk' ? String(data.get('preferredPark') ?? '') : '' }
      : { ...common, excursion });
    if (!parsed.success || offer === '') {
      const nextErrors: FieldErrors = {};
      if (offer === '') nextErrors.excursion = selectPrompts[category];
      for (const issue of parsed.error?.issues ?? []) nextErrors[String(issue.path[0])] ??= issue.message;
      setErrors(nextErrors); setState('idle'); return;
    }
    setErrors({}); setState('sending');
    try {
      const response = await fetch('/api/leads', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(parsed.data) });
      if (response.status !== 201) {
        const body = await response.json() as ApiError;
        setErrors(body.error.fields ?? {});
        throw new Error(body.error.message);
      }
      trackGoal('lead_submit');
      setState('success');
    } catch {
      setState('error');
    }
  }

  function reset() {
    setCategory(initialCategory); setOffer(initialOffer ?? ''); setIsPicking(initialOffer === null);
    setBookingFormat(''); setState('idle');
  }

  if (state === 'success') return <div className="form-success" role="status" tabIndex={-1} ref={successRef}>
    <h3>Заявка принята</h3><p>{successTexts[category]}</p>
    <button className="text-button" type="button" onClick={reset}>Отправить ещё одну</button>
  </div>;

  return <form ref={formRef} className="lead-form" onSubmit={submit} noValidate aria-busy={state === 'sending'}>
    <fieldset className="booking-fields" disabled={state === 'sending'}><legend className="booking-sr-only">Данные заявки</legend>
      <div className="form-grid">
        {isPicking ? <>
          {categories.length > 1 && <fieldset className="booking-segments booking-segments--category full"><legend className="booking-sr-only">Что выбираете</legend>
            <div className="booking-segment-options">
              {categories.map((item) => <label key={item.id}><input type="radio" name="offerCategory" value={item.id} checked={category === item.id} onChange={() => pickCategory(item.id)} /><span>{item.label}</span></label>)}
            </div>
          </fieldset>}
          <label className="full"><span className="booking-sr-only">{selectPrompts[category]}</span>
            <select name="excursion" value={offer} required onChange={(event) => setOffer(event.target.value as Offer)} aria-invalid={Boolean(errors.excursion)} aria-describedby="excursion-error">
              <option value="" disabled>{selectPrompts[category]}</option>
              {groups.map((group) => <optgroup key={group.label} label={group.label}>{group.offers.map((value) => <option key={value} value={value}>{offerTitle(value)}</option>)}</optgroup>)}
            </select>
            <small id="excursion-error">{errors.excursion}</small>
          </label>
        </> : offer !== '' && <div className="booking-offer-summary full">
          <p><span>{isCourse(offer) ? 'Курс школы «Добро и Небо»' : offerCategories.find((item) => item.id === category)!.label}</span><strong>{offerTitle(offer)}</strong></p>
          <button type="button" className="booking-change" onClick={startPicking} aria-label={`Изменить выбор: ${offerTitle(offer)}`}>Изменить</button>
        </div>}
        {excursionFields && offer === 'botanical_walk' && <label className="full"><span className="booking-sr-only">Парк · необязательно</span><input name="preferredPark" list="booking-parks" maxLength={200} placeholder="Парк · необязательно" aria-invalid={Boolean(errors.preferredPark)} aria-describedby="park-error" /><datalist id="booking-parks"><option value="Чистяковская роща" /><option value="Солнечный остров" /><option value="Ботанический сад" /></datalist><small id="park-error">{errors.preferredPark}</small></label>}
        {excursionFields && <fieldset className="booking-segments booking-format full"><legend>Формат</legend>
          <div className="booking-segment-options">
            <label title="Для вашей уже существующей группы: класса, коллег или компании"><input type="radio" name="bookingFormat" value="group" required checked={bookingFormat === 'group'} onChange={() => setBookingFormat('group')} aria-invalid={Boolean(errors.bookingFormat)} aria-describedby="format-help format-error" /><span>Групповая</span></label>
            <label title="Для вас или вашего личного круга, без присоединения к незнакомым участникам"><input type="radio" name="bookingFormat" value="individual" required checked={bookingFormat === 'individual'} onChange={() => setBookingFormat('individual')} aria-invalid={Boolean(errors.bookingFormat)} aria-describedby="format-help format-error" /><span>Индивидуальная</span></label>
          </div>
          <span className="booking-sr-only" id="format-help">Групповая — для вашей существующей группы, заявка отправляется через форму. Индивидуальная — только по согласованию с менеджером по телефону или в мессенджере.</span><small id="format-error">{errors.bookingFormat}</small>
        </fieldset>}
        {excursionFields && bookingFormat === 'individual' ? <div className="booking-individual-note full" role="status">
          <p><strong>Индивидуальные экскурсии согласуются только с менеджером.</strong> Позвоните или напишите — подберём дату, программу и стоимость для вашей компании.</p>
          <p><a href="tel:+79086935909">8 (908) 693-59-09</a> · <a href="https://t.me/krd_iznanka" target="_blank" rel="noreferrer">Telegram</a> · <a href="https://max.ru/u/f9LHodD0cOL8_Fj35RGhuyLiOq6qCp7gWf2nDYLUYDw854d6hI6P_2QzJHQ" target="_blank" rel="noreferrer">MAX</a></p>
        </div> : <>
        <label>Имя<input name="name" autoComplete="name" required maxLength={100} aria-invalid={Boolean(errors.name)} aria-describedby="name-error" /><small id="name-error">{errors.name}</small></label>
        <label>Контакт<input name="contact" autoComplete="tel" required maxLength={160} aria-invalid={Boolean(errors.contact)} aria-describedby="contact-error" placeholder="+7 или 8… либо @ник" title="Телефон с +7 или 8 (11 цифр) либо ник в мессенджере" /><small id="contact-error">{errors.contact}</small></label>
        <label className="full"><span className="booking-sr-only">Комментарий · необязательно</span><textarea name="comment" rows={1} maxLength={1000} placeholder="Комментарий · необязательно" aria-invalid={Boolean(errors.comment)} aria-describedby="comment-error" /><small id="comment-error">{errors.comment}</small></label>
        <label className="honeypot" aria-hidden="true">Сайт<input name="website" tabIndex={-1} autoComplete="off" /></label>
        <fieldset className="booking-consents full"><legend>Я согласен с:</legend>
          <label className="check"><input name="privacyAccepted" type="checkbox" required aria-invalid={Boolean(errors.privacyAccepted)} aria-describedby={errors.privacyAccepted ? 'privacy-error' : undefined} /><span><a href="/consent" onClick={onLegalLink}>Обработкой персональных данных</a></span></label>
          {errors.privacyAccepted && <p className="field-error" id="privacy-error">{errors.privacyAccepted}</p>}
        </fieldset>
        {excursionFields && <p className="booking-media-note full"><a href="/media-consent" onClick={onLegalLink}>Согласие на фото- и видеосъёмку</a> оформляется отдельно и добровольно на месте.</p>}
        </>}
      </div>
      {!(excursionFields && bookingFormat === 'individual') && <button className="button button--primary" disabled={state === 'sending'}>{state === 'sending' ? 'Отправляем…' : 'Отправить заявку'}</button>}
    </fieldset>
    {state === 'error' && <p className="form-error" role="alert">Данные сохранены в форме. Проверьте заполнение полей и соединение, затем повторите отправку.</p>}
  </form>;
}
